import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
  getSocket,
  refreshAdminSocketAuth,
} from '../../containers/ChatPage/socket';

const CLIENT_ID_KEY = 'admin_system_presence_client_id';
const HEARTBEAT_MS = 15000;
const ACTIVITY_EMIT_MS = 10000;

const getClientId = () => {
  try {
    const stored = window.sessionStorage.getItem(CLIENT_ID_KEY);
    if (stored) return stored;

    const next = typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `admin_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;

    window.sessionStorage.setItem(CLIENT_ID_KEY, next);
    return next;
  } catch (_error) {
    return `admin_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }
};

const detectBrowser = () => {
  const ua = String(navigator?.userAgent || '');
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/OPR\//i.test(ua)) return 'Opera';
  if (/Firefox\//i.test(ua)) return 'Firefox';
  if (/Chrome\//i.test(ua)) return 'Chrome';
  if (/Safari\//i.test(ua)) return 'Safari';
  return 'Browser';
};

const detectDevice = () => {
  const ua = String(navigator?.userAgent || '');
  const platform = String(navigator?.userAgentData?.platform || navigator?.platform || '');

  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua) || (/Mac/i.test(platform) && Number(navigator?.maxTouchPoints || 0) > 1)) return 'iPad';
  if (/Windows/i.test(ua) || /Win/i.test(platform)) return 'Windows PC';
  if (/Mac/i.test(ua) || /Mac/i.test(platform)) return 'Mac';
  if (/Linux/i.test(ua) || /Linux/i.test(platform)) return 'Linux';
  return platform || 'Device';
};

const AdminSystemPresenceBridge = () => {
  const location = useLocation();
  const authenticated = useSelector((state) => state.auth?.authenticate === true);
  const routeRef = useRef('');
  const emitRef = useRef(null);
  const lastActivityEmitRef = useRef(0);

  routeRef.current = `${location.pathname || '/'}${location.search || ''}`;

  useEffect(() => {
    if (!authenticated) return undefined;

    refreshAdminSocketAuth();
    const socket = getSocket();
    const clientId = getClientId();
    const deviceLabel = detectDevice();
    const browserLabel = detectBrowser();
    let heartbeatTimer = null;

    const emitPresence = ({ activity = false } = {}) => {
      if (!socket?.connected) return;

      socket.emit('system:presence', {
        clientId,
        route: routeRef.current,
        visibility: document.hidden ? 'hidden' : 'visible',
        deviceLabel,
        browserLabel,
        activity,
      });
    };

    emitRef.current = emitPresence;

    const onConnect = () => emitPresence({ activity: true });
    const onVisibility = () => emitPresence({ activity: false });
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityEmitRef.current < ACTIVITY_EMIT_MS) return;
      lastActivityEmitRef.current = now;
      emitPresence({ activity: true });
    };

    socket.on('connect', onConnect);
    if (socket.connected) emitPresence({ activity: true });

    heartbeatTimer = window.setInterval(() => emitPresence({ activity: false }), HEARTBEAT_MS);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pointerdown', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('scroll', onActivity, { passive: true });

    return () => {
      emitRef.current = null;
      socket.off('connect', onConnect);
      if (heartbeatTimer) window.clearInterval(heartbeatTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('scroll', onActivity);
    };
  }, [authenticated]);

  useEffect(() => {
    if (authenticated) emitRef.current?.({ activity: false });
  }, [authenticated, location.pathname, location.search]);

  return null;
};

export default AdminSystemPresenceBridge;
